import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axiosClient from "../../../../api/axiosClient";
import skuAttributeService from "../../../ppe/pages/masters/sku-attributes/skuAttributeService";

export default function BOMMaterialSelectorModal({
  show,
  onClose,
  onConfirm,
}) {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const [attributes, setAttributes] = useState([]);
  const [filters, setFilters] = useState([]);
  const [searchText, setSearchText] = useState("");

  const [isMaximized, setIsMaximized] = useState(false);

  // ============================================================
  // LOAD DATA
  // ============================================================
  useEffect(() => {
    if (!show) return;
    loadAttributes();
    loadMaterials();
  }, [show]);

  useEffect(() => {
    if (!show) return;

    const t = setTimeout(() => {
      loadMaterials();
    }, 400);

    return () => clearTimeout(t);
  }, [filters, searchText]);

  // ============================================================
  // API CALL
  // ============================================================
  const loadMaterials = async () => {
    try {
      setLoading(true);

      const cleanedFilters = filters.filter(
        (f) => f.value && f.value !== ""
      );

      const res = await axiosClient.post(
        "/api/ppe/masters/materials/search-by-attributes",
        {
          filters: cleanedFilters,
          search: searchText,
        }
      );

      setMaterials(res.data?.data || []);
    } catch (err) {
      console.error(err);
      setMaterials([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAttributes = async () => {
    try {
      const res = await skuAttributeService.getAttributesForEntity("RAW_MATERIAL");

      if (res.success) {
        setAttributes(res.data || []);
      } else {
        setAttributes([]);
      }
    } catch (err) {
      console.error(err);
      setAttributes([]);
    }
  };

  // ============================================================
  // FILTER HANDLER
  // ============================================================
  const handleFilterChange = (attributeId, value) => {
    const val = value?.trim?.() || value;

    setFilters((prev) => {
      const updated = [...prev];
      const index = updated.findIndex((f) => f.attribute_id === attributeId);

      if (index >= 0) updated[index].value = val;
      else updated.push({ attribute_id: attributeId, value: val });

      return updated;
    });
  };

  // ============================================================
  // SELECTION
  // ============================================================
  const toggleSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((i) => i !== id)
        : [...prev, id]
    );
  };

  const handleConfirm = () => {
    const selected = materials.filter((m) =>
      selectedIds.includes(m.id)
    );
    onConfirm(selected);
    setSelectedIds([]);
  };

  if (!show) return null;

  // ============================================================
  // PORTAL RENDER
  // ============================================================
  return createPortal(
    <div className="modal-overlay">

      <div className={`modal-box ${isMaximized ? "maximized" : ""}`}>

        {/* HEADER */}
        <div className="modal-header">
          <div>
            <h5>Select Raw Materials</h5>
            <small>Filter by attributes or search globally</small>
          </div>

          <div>
            <button onClick={() => setIsMaximized(!isMaximized)}>
              {isMaximized ? "🗗" : "🗖"}
            </button>
            <button onClick={onClose}>✕</button>
          </div>
        </div>

        {/* FILTER */}
        <div className="filter-section">

          <input
            className="search-input"
            placeholder="Search by name/code..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />

          <div className="filter-row">
            {attributes
              .filter((attr) =>
                [
                  "Manufacturer",
                  "Make",
                  "Model",
                  "Category",
                  "Type",
                  "Thickness",
                  "Color",
                ].includes(attr.attribute_name)
              )
              .map((attr) => (
                <div key={attr.id} className="filter-field">

                  <label>{attr.attribute_name}</label>

                  {attr.data_type === "LIST" ? (
                    <select
                      value={
                        filters.find((f) => f.attribute_id === attr.id)?.value || ""
                      }
                      onChange={(e) =>
                        handleFilterChange(attr.id, e.target.value)
                      }
                    >
                      <option value="">All</option>
                      {attr.values?.map((v) => (
                        <option key={v.id} value={v.value}>
                          {v.value}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      placeholder="Enter"
                      onChange={(e) =>
                        handleFilterChange(attr.id, e.target.value)
                      }
                    />
                  )}
                </div>
              ))}
          </div>
        </div>

        {/* BODY */}
        <div className="modal-body">
          {loading ? (
            <div className="empty">Loading materials...</div>
          ) : materials.length === 0 ? (
            <div className="empty">No materials found</div>
          ) : (
            materials.map((m) => (
              <div key={m.id} className="material-row">

                <input
                  type="checkbox"
                  checked={selectedIds.includes(m.id)}
                  onChange={() => toggleSelection(m.id)}
                />

                <div>
                  <strong>{m.name}</strong>
                  <div className="sub">{m.code}</div>
                  {m.attributes_summary && (
                    <div className="attr-summary">
                      {m.attributes_summary}
                    </div>
                  )}
                </div>

              </div>
            ))
          )}
        </div>

        {/* FOOTER */}
        <div className="modal-footer">
          <span>{selectedIds.length} selected</span>

          <div>
            <button onClick={onClose}>Cancel</button>

            <button
              className="btn-primary"
              disabled={selectedIds.length === 0}
              onClick={handleConfirm}
            >
              Add Selected ({selectedIds.length})
            </button>
          </div>
        </div>

      </div>

      {/* STYLES */}
      <style>{`
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.45);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 9999;
        }

        .modal-box {
          width: 900px;
          max-height: 90vh;
          background: #fff;
          border-radius: 10px;
          display: flex;
          flex-direction: column;
        }

        .modal-box.maximized {
          width: 95%;
          height: 95%;
        }

        .modal-header {
          padding: 12px;
          border-bottom: 1px solid #eee;
          display: flex;
          justify-content: space-between;
        }

        .filter-section {
          padding: 12px;
          background: #f9fafb;
          border-bottom: 1px solid #eee;
        }

        .search-input {
          width: 100%;
          padding: 8px;
          margin-bottom: 10px;
          border: 1px solid #ddd;
          border-radius: 6px;
        }

        .filter-row {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }

        .filter-field {
          display: flex;
          flex-direction: column;
          min-width: 160px;
          flex: 1;
        }

        .filter-field label {
          font-size: 12px;
          margin-bottom: 4px;
          color: #374151;
        }

        .filter-field input,
        .filter-field select {
          padding: 6px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
        }

        .modal-body {
          overflow-y: auto;
          flex: 1;
          padding: 12px;
        }

        .material-row {
          display: flex;
          gap: 10px;
          padding: 10px;
          border-bottom: 1px solid #eee;
        }

        .attr-summary {
          font-size: 11px;
          color: #6b7280;
        }

        .modal-footer {
          padding: 10px;
          display: flex;
          justify-content: space-between;
          border-top: 1px solid #eee;
        }

        .btn-primary {
          background: #2563eb;
          color: white;
          padding: 6px 12px;
          border-radius: 6px;
          border: none;
        }

        .empty {
          text-align: center;
          color: #6b7280;
        }
      `}</style>

    </div>,
    document.body
  );
}